import React from "react";
import { Link } from "react-router-dom";
import MarketingLayout from "./components/MarketingLayout.js";
import MarketingFooterCtas from "./components/MarketingFooterCtas.js";
import "./Resources.css";

export default function Resources() {
  const coreResources = [
    {
      title: "Product overview",
      description:
        "What Zolaid is: AI chat, workspace, builders, and apps—described without promising features the app does not ship.",
      link: "/Overview",
      category: "Product",
    },
    {
      title: "Docs",
      description:
        "In-product documentation for Website Builder, Code (mini-IDE), and Terminal—not a full external developer portal unless we publish one separately.",
      link: "/docs",
      category: "Documentation",
    },
    {
      title: "Apps",
      description: "Browse connected tools and use them from chat where supported.",
      link: "/Apps",
      category: "Product",
    },
    {
      title: "Integrations",
      description:
        "Overview of tools we highlight (e.g. GitHub, Slack, Drive, Figma, Notion, Jira). Coverage varies by integration and your setup.",
      link: "/Integrations",
      category: "Product",
    },
    {
      title: "Features",
      description: "Interactive and visual tour of major capabilities in the product.",
      link: "/Features",
      category: "Product",
    },
    {
      title: "Support",
      description: "Help center: docs shortcuts, FAQs, and how to reach us—no separate ticket system is promised here.",
      link: "/Support",
      category: "Help",
    },
    {
      title: "Blog",
      description: "Updates and articles. Longer reads may link to dedicated pages (e.g. tech articles).",
      link: "/Blog",
      category: "Content",
    },
    {
      title: "Use Zolaid on the web",
      description: "Zolaid is a website. Open it in your browser from the homepage or chat.",
      link: "/Download",
      category: "Product",
    },
    {
      title: "Pricing",
      description: "Public plans and billing—confirm details on the pricing page before buying.",
      link: "/pricing",
      category: "Billing",
    },
    {
      title: "Contact",
      description: "Questions, sales, and feedback. We reply over email or the channels we list there.",
      link: "/Contact",
      category: "Help",
    },
  ];

  const orgResources = [
    {
      title: "Teams",
      description: "How groups can think about shared use of Zolaid—aligned with the Teams marketing page, not a separate enterprise suite.",
      link: "/Teams",
      category: "Organization",
    },
    {
      title: "Education",
      description: "Education-oriented positioning and how to apply—subject to verification and current policy.",
      link: "/Education",
      category: "Organization",
    },
    {
      title: "Enterprise",
      description: "Honest scope for organizations: what the product includes today and what we discuss when you contact us.",
      link: "/Enterprise",
      category: "Organization",
    },
    {
      title: "Roadmap",
      description: "Directional product plans—timelines and priorities can change.",
      link: "/Roadmap",
      category: "Product",
    },
    {
      title: "Sample deep-dive article",
      description: "Example long-form content (Socratic method article)—illustrative, not a full help library.",
      link: "/TechArticlesPage",
      category: "Content",
    },
  ];

  const experimentalResources = [
    {
      title: "Templates",
      description: "Template save/load experiments; may lack auth or production polish.",
      link: "/Templates",
    },
    {
      title: "Projects",
      description: "Disk-backed builder project experiments.",
      link: "/Projects",
    },
    {
      title: "Campaigns",
      description: "Bundled asset experiments.",
      link: "/Campaigns",
    },
    {
      title: "Impact simulator",
      description: "Goal/simulation prototype.",
      link: "/Impact",
    },
    {
      title: "Moderation",
      description: "Internal-style moderation UI; not a live marketplace.",
      link: "/Moderation",
    },
  ];

  return (
    <MarketingLayout>
      <div className="resources-page resources-content">
        <header className="marketing-page-intro">
          <p className="marketing-page-eyebrow">Resources</p>
          <h1>Resources</h1>
          <p className="page-lead" style={{ marginBottom: 0 }}>
            Documentation, product pages, and a few lab routes — labeled clearly so you know what is
            production-ready versus experimental.
          </p>
        </header>

        <section className="resources-section" aria-labelledby="resources-core-heading">
          <h2 id="resources-core-heading" className="resources-section-title">
            Core resources
          </h2>
          <p className="resources-section-lead">
            Start here for product truth, help, and billing. Descriptions match what you can verify in the product—not a generic enterprise resource portal.
          </p>
          <div className="resources-grid">
            {coreResources.map((item) => (
              <ResourceCard key={item.link} {...item} />
            ))}
          </div>
        </section>

        <section className="resources-section" aria-labelledby="resources-org-heading">
          <h2 id="resources-org-heading" className="resources-section-title">
            Teams, education, and more
          </h2>
          <p className="resources-section-lead">
            Organizational and community pages. For custom quotes or security review, use Enterprise or Contact.
          </p>
          <div className="resources-grid">
            {orgResources.map((item) => (
              <ResourceCard key={item.link} {...item} />
            ))}
          </div>
        </section>

        <section className="resources-section resources-experimental" aria-labelledby="resources-lab-heading">
          <h2 id="resources-lab-heading" className="resources-section-title">
            Lab &amp; experiments
          </h2>
          <p className="resources-section-lead">
            These URLs ship for demos and internal iteration. Behavior, auth, and data handling may change or break—don&apos;t rely on them for production workflows.
          </p>
          <ul className="resources-experimental-list">
            {experimentalResources.map((item) => (
              <li key={item.link}>
                <Link to={item.link} className="resources-experimental-link">
                  {item.title}
                </Link>
                <span className="resources-experimental-desc"> — {item.description}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="quick-links-section">
          <h2>Quick links</h2>
          <div className="quick-links-grid">
            <QuickLink title="Docs" link="/docs" />
            <QuickLink title="Overview" link="/Overview" />
            <QuickLink title="Pricing" link="/pricing" />
            <QuickLink title="Integrations" link="/Integrations" />
            <QuickLink title="Teams" link="/Teams" />
            <QuickLink title="Enterprise" link="/Enterprise" />
            <QuickLink title="Roadmap" link="/Roadmap" />
            <QuickLink title="Privacy" link="/PrivacyPolicy" />
            <QuickLink title="Terms" link="/TermsOfService" />
            <QuickLink title="Contact" link="/Contact" />
          </div>
        </section>

        <section className="resources-faq">
          <h2>Frequently asked questions</h2>
          <p className="resources-section-lead resources-faq-intro">
            Short answers; for legal or security commitments, rely on your agreement and our Privacy Policy / Terms—not marketing copy.
          </p>
          <div className="faq-list">
            <FAQItem
              question="How do I get started?"
              answer="Create an account, open the chatbot or workspace from the home flow, and read Docs for Builder, Code, and Terminal. Choose a plan on the Pricing page when you are ready."
            />
            <FAQItem
              question="Can Zolaid write or explain code?"
              answer="Yes, in the assistant and workspace context the model can generate and discuss code in many common languages. You should still review and test anything you ship."
            />
            <FAQItem
              question="Is my data secure?"
              answer="We use standard HTTPS for the web app. Retention, subprocessors, and compliance depend on your deployment and plan—read the Privacy Policy and ask us in writing for enterprise questions."
            />
            <FAQItem
              question="Can I connect other tools?"
              answer="Browse Apps and Integrations for what we surface today. Not every tool is available in every environment; Enterprise or Contact is the right place to ask about gaps."
            />
            <FAQItem
              question="How do I get help?"
              answer="Use the Support page and Contact. Response times depend on channel and plan—we do not promise 24/7 dedicated support on this marketing site."
            />
            <FAQItem
              question="Can I cancel a subscription?"
              answer="If you subscribed through Stripe or another provider, cancellation follows that provider’s flow and your billing period. Check Pricing and account settings for the current behavior."
            />
          </div>
        </section>

        <MarketingFooterCtas />
      </div>
    </MarketingLayout>
  );
}

function ResourceCard({ title, description, link, category }) {
  return (
    <Link to={link} className="resource-card">
      {category && <span className="resource-category">{category}</span>}
      <h2>{title}</h2>
      <p>{description}</p>
    </Link>
  );
}

function QuickLink({ title, link }) {
  return (
    <Link to={link} className="quick-link">
      {title}
    </Link>
  );
}

function FAQItem({ question, answer }) {
  return (
    <div className="faq-item">
      <h3 className="faq-question">{question}</h3>
      <p className="faq-answer">{answer}</p>
    </div>
  );
}
